import { acceptHMRUpdate, defineStore } from 'pinia';
import { computed } from 'vue';

import { PrettyTemplate } from '~/domain';
import { PlanType } from '~/services';
import { useAuthStore } from '~/stores/auth.store';
import { usePrimordialSoupStore } from '~/stores/primordial-soup.store.ts';

export const useAuthorizationStore = defineStore('authorization', () => {
	/**
	 * Store used in order to centralize app user authorizations.
	 * Meant to centralize general business logic contextualized by stores (e.g: auth.store, organization.store...)
	 * This should mainly be used by authorization dedicated composables or components across the app
	 */

	/** Imports other stores */
	const authStore = useAuthStore();
	const primordialSoupStore = usePrimordialSoupStore();

	/** Authorization computed */
	const isAdmin = computed<boolean>(() => !!authStore.userProfile?.isAdmin);
	const isAuthenticated = computed(() => !!authStore.userProfile);
	const hasBilling = computed<boolean>(() => isAdmin.value && !!authStore.userProfile?.organizationCreatedAt);
	const hasConfigurableTemplates = computed<boolean>(
		() => isAdmin.value && !!primordialSoupStore.templates?.some((template: PrettyTemplate) => template.plan === PlanType.MY_EXPENSE)
	);

	/** Authorization functions */
	const amI = (userId: string): boolean => {
		return authStore.userProfile?.id && userId === authStore.userProfile.id;
	};

	return {
		/** Refs and computed */
		hasBilling,
		hasConfigurableTemplates,
		isAdmin,
		isAuthenticated,

		/** Authorization functions */
		amI
	};
});

if (import.meta.hot) import.meta.hot.accept(acceptHMRUpdate(useAuthorizationStore, import.meta.hot));
